/**
 * SegmentedToggle — a track with a pill that SLIDES to the selected option.
 *
 * The pill is one absolutely-positioned element moved with `translateX`, not a background on the
 * active button: a transform animates on the compositor, and it is what makes the control read as
 * one switch rather than four buttons. With no matching option the pill fades out instead of
 * snapping to index 0, so an off-list value never lies about what is selected.
 */

import { CONTROL_H, TRACK, ACCENT_BG, ACCENT_ON, ACCENT_TEXT, SEGMENT_OFF, SEGMENT_DISABLED, MOTION } from './tokens';
import type { OptionItem } from './OptionList';

interface SegmentedToggleProps<T> {
  options: readonly OptionItem<T>[];
  /** Selected value; anything not in `options` hides the pill. */
  value: T;
  onSelect: (value: T) => void;
  /** Replaces ONE segment's label — the Textures row shows load progress on the resolution being
   *  fetched while the pill stays on what the world is actually drawn with. */
  overrideLabel?: { value: T; label: string };
  /** Locks the whole control (a load is in flight). */
  busy?: boolean;
  ariaLabel?: string;
}

export function SegmentedToggle<T extends string | number>({
  options, value, onSelect, overrideLabel, busy, ariaLabel,
}: SegmentedToggleProps<T>) {
  const index = options.findIndex((o) => o.value === value);
  const width = 100 / options.length;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`relative ${CONTROL_H} ${TRACK} p-1 ${busy ? 'opacity-60' : ''}`}
    >
      <div className="absolute inset-1 pointer-events-none">
        <div
          className={`h-full rounded-full ${ACCENT_BG} shadow-sm ${MOTION} ${index < 0 ? 'opacity-0' : 'opacity-100'}`}
          style={{ width: `${width}%`, transform: `translateX(${Math.max(0, index) * 100}%)` }}
        />
      </div>
      <div className="relative h-full flex">
        {options.map((o, i) => (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={i === index}
            disabled={busy}
            onClick={() => onSelect(o.value)}
            className={`flex-1 min-w-0 h-full rounded-full text-xs md:text-[11px] font-bold ${MOTION} ${
              i === index ? ACCENT_ON
                // The overridden segment is the one being loaded TO — accented, not greyed, or the
                // progress it carries would be the faintest thing in the control.
                : overrideLabel?.value === o.value ? ACCENT_TEXT
                : busy ? SEGMENT_DISABLED
                : `${SEGMENT_OFF} cursor-pointer hover:text-neutral-800`
            }`}
          >
            <span className="block truncate px-1">
              {overrideLabel && overrideLabel.value === o.value ? overrideLabel.label : o.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
